import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  GiftIcon,
  MailIcon,
  Trash2Icon,
  RefreshCwIcon,
  CopyIcon,
  PlusIcon,
  XIcon,
  CheckCircle2Icon,
  ClockIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Ultra Admin token'ı — layout ile aynı anahtar.
const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('ultra_admin_token')}`,
});

const PLAN_OPTIONS = [
  { slug: 'solo', label: 'Solo' },
  { slug: 'starter', label: 'Starter' },
  { slug: 'professional', label: 'Professional' },
  { slug: 'enterprise', label: 'Enterprise' },
];

const DURATION_UNITS = [
  { id: 'day', label: 'Gün' },
  { id: 'month', label: 'Ay' },
  { id: 'year', label: 'Yıl' },
];

const STATUS_META = {
  sent:      { label: 'Gönderildi', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  pending:   { label: 'Beklemede',  className: 'bg-slate-50 text-slate-700 border-slate-200' },
  accepted:  { label: 'Kullanıldı', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  expired:   { label: 'Süresi Doldu', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  revoked:   { label: 'İptal',      className: 'bg-rose-50 text-rose-700 border-rose-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch { return iso; }
};

const UltraPromoCodes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invite_email: '',
    invite_name: '',
    selected_plan: 'solo',
    free_days: 30,
    discount_percent: 0,
    discount_duration_unit: 'month',
    discount_duration_value: 1,
    expires_days: 14,
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ultra-admin/promo-codes`, {
        headers: authHeader(),
      });
      setRows(res.data.promo_codes || []);
    } catch (e) {
      console.error(e);
      toast.error('Promosyon listesi alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const resetForm = () => setForm({
    invite_email: '',
    invite_name: '',
    selected_plan: 'solo',
    free_days: 30,
    discount_percent: 0,
    discount_duration_unit: 'month',
    discount_duration_value: 1,
    expires_days: 14,
  });

  const submit = async () => {
    if (!form.invite_email.trim()) {
      toast.error('E-posta zorunlu'); return;
    }
    if ((form.free_days || 0) === 0 && (form.discount_percent || 0) === 0) {
      toast.error('En az ücretsiz gün veya % indirim gir'); return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/promo-codes`,
        {
          ...form,
          free_days: Number(form.free_days || 0),
          discount_percent: Number(form.discount_percent || 0),
          discount_duration_value: Number(form.discount_duration_value || 0),
          expires_days: Number(form.expires_days || 14),
        },
        { headers: authHeader() },
      );
      if (res.data.email_sent) {
        toast.success('Promosyon oluşturuldu ve e-posta gönderildi');
      } else {
        toast.warning('Promosyon oluşturuldu — e-posta gönderilemedi (kodu manuel paylaşabilirsiniz)');
      }
      setModalOpen(false);
      resetForm();
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const resend = async (id) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/ultra-admin/promo-codes/${id}/resend`, {},
        { headers: authHeader() },
      );
      if (res.data.email_sent) toast.success('E-posta yeniden gönderildi');
      else toast.error('E-posta gönderilemedi');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gönderim başarısız');
    }
  };

  const revoke = async (id) => {
    if (!window.confirm('Bu davet kodunu iptal etmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ultra-admin/promo-codes/${id}`, {
        headers: authHeader(),
      });
      toast.success('Silindi');
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silinemedi');
    }
  };

  const copyCode = (code) => {
    try {
      navigator.clipboard.writeText(code);
      toast.success(`Kod kopyalandı: ${code}`);
    } catch {
      toast.info(code);
    }
  };

  const active = rows.filter((r) => ['sent', 'pending'].includes(r.status)).length;
  const used = rows.filter((r) => r.status === 'accepted').length;

  return (
    <div className="p-6 space-y-6" data-testid="ultra-promo-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GiftIcon className="w-6 h-6 text-emerald-600" />
            Promosyonlar
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Ücretsiz kullanım veya indirim ile plan davetleri oluşturun ve e-posta ile gönderin.
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setModalOpen(true); }}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="promo-create-btn"
        >
          <PlusIcon className="w-4 h-4 mr-1" /> Yeni Davet
        </Button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-50 text-blue-700"><MailIcon className="w-5 h-5" /></div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Toplam Gönderim</div>
              <div className="text-xl font-semibold">{rows.length}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-50 text-emerald-700"><CheckCircle2Icon className="w-5 h-5" /></div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Kullanılan</div>
              <div className="text-xl font-semibold">{used}</div>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-50 text-amber-700"><ClockIcon className="w-5 h-5" /></div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Bekleyen</div>
              <div className="text-xl font-semibold">{active}</div>
            </div>
          </div>
        </Card>
      </div>

      {/* List */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Davetler
          </h2>
          <button
            onClick={fetchRows}
            className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1"
          >
            <RefreshCwIcon className="w-3.5 h-3.5" /> Yenile
          </button>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Henüz davet yok. Sağ üstteki <strong>Yeni Davet</strong> butonundan başlayın.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="promo-list-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Kod</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Davetli</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Plan</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Avantajlar</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Oluşturma</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Bitiş</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Durum</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  return (
                    <tr key={r.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => copyCode(r.code)}
                          className="font-mono font-semibold text-emerald-700 hover:underline flex items-center gap-1"
                          title="Kopyala"
                        >
                          {r.code} <CopyIcon className="w-3 h-3 opacity-60" />
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.invite_name || '—'}</div>
                        <div className="text-xs text-gray-500">{r.invite_email}</div>
                      </td>
                      <td className="px-4 py-3 capitalize">{r.selected_plan}</td>
                      <td className="px-4 py-3 text-xs text-gray-700 space-y-1">
                        {r.free_days > 0 && (
                          <div>🎁 {r.free_days} gün ücretsiz</div>
                        )}
                        {r.discount_percent > 0 && (
                          <div>💸 %{r.discount_percent} indirim / {r.discount_duration_value} {({day:'gün',month:'ay',year:'yıl'})[r.discount_duration_unit] || r.discount_duration_unit}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(r.expires_at)}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${meta.className}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                        {['sent', 'pending'].includes(r.status) && (
                          <>
                            <button
                              onClick={() => resend(r.id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 transition"
                              title="Bu davete yeniden e-posta gönder"
                              data-testid={`promo-resend-${r.id}`}
                            >
                              <MailIcon className="w-3.5 h-3.5" />
                              Tekrar Gönder
                            </button>
                            <button
                              onClick={() => revoke(r.id)}
                              className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition"
                              title="İptal et"
                              data-testid={`promo-revoke-${r.id}`}
                            >
                              <Trash2Icon className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Create Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="promo-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Yeni Promosyon Daveti</h3>
              <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Davetli Adı</Label>
                  <Input
                    value={form.invite_name}
                    onChange={(e) => setForm({ ...form, invite_name: e.target.value })}
                    placeholder="Ad Soyad (opsiyonel)"
                    data-testid="promo-invite-name"
                  />
                </div>
                <div>
                  <Label>E-posta *</Label>
                  <Input
                    type="email"
                    value={form.invite_email}
                    onChange={(e) => setForm({ ...form, invite_email: e.target.value })}
                    placeholder="ornek@sirket.com"
                    data-testid="promo-invite-email"
                  />
                </div>
              </div>

              <div>
                <Label>Plan *</Label>
                <select
                  value={form.selected_plan}
                  onChange={(e) => setForm({ ...form, selected_plan: e.target.value })}
                  className="w-full mt-1 h-10 px-3 border rounded-md text-sm bg-white"
                  data-testid="promo-plan"
                >
                  {PLAN_OPTIONS.map((p) => (
                    <option key={p.slug} value={p.slug}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Ücretsiz Gün</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.free_days}
                    onChange={(e) => setForm({ ...form, free_days: e.target.value })}
                    placeholder="Ör: 30"
                    data-testid="promo-free-days"
                  />
                  <p className="text-xs text-gray-500 mt-1">Standart 14 günlük denemenin üstüne eklenir.</p>
                </div>
                <div>
                  <Label>Davet Süresi (gün)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={form.expires_days}
                    onChange={(e) => setForm({ ...form, expires_days: e.target.value })}
                    placeholder="Ör: 14"
                    data-testid="promo-expires-days"
                  />
                  <p className="text-xs text-gray-500 mt-1">Bu süreden sonra kod çalışmaz.</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label>% İndirim (opsiyonel — trial sonrası)</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input
                    type="number"
                    min="0" max="100"
                    value={form.discount_percent}
                    onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                    placeholder="%"
                    data-testid="promo-discount-percent"
                  />
                  <Input
                    type="number"
                    min="0"
                    value={form.discount_duration_value}
                    onChange={(e) => setForm({ ...form, discount_duration_value: e.target.value })}
                    placeholder="Süre"
                    disabled={!Number(form.discount_percent)}
                    data-testid="promo-discount-value"
                  />
                  <select
                    value={form.discount_duration_unit}
                    onChange={(e) => setForm({ ...form, discount_duration_unit: e.target.value })}
                    className="h-10 px-2 border rounded-md text-sm bg-white disabled:opacity-50"
                    disabled={!Number(form.discount_percent)}
                    data-testid="promo-discount-unit"
                  >
                    {DURATION_UNITS.map((u) => (
                      <option key={u.id} value={u.id}>{u.label}</option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-1">Örn: %25 indirim, 3 ay boyunca (trial sonrası devreye girer).</p>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Vazgeç</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="promo-submit-btn"
              >
                {saving ? 'Gönderiliyor…' : 'Oluştur & E-posta Gönder'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UltraPromoCodes;
